/*
 * How Studio's pages pace their polls, so a hundred authors with jobs open do not flood the
 * API: slower while a job only waits (in a queue, or while its MP4s export after the previews
 * are ready), and not at all while the browser tab is hidden.
 */

/** The first poll's delay while a job waits; each poll that finds it still waiting waits longer. */
export const POLL_WAIT_MS = 2000;
/** The longest a page waits between polls. */
export const POLL_MAX_MS = 10000;

/** A poll delay, and what it was waiting for. */
export interface Backoff {
  key: string;
  delay: number;
}

/**
 * The delay before the next poll while `key` (e.g. the job's stage) is waited for: `start`
 * at first, then `factor` times longer each poll that finds the same thing, up to POLL_MAX_MS.
 */
export function backoff(
  previous: Backoff | null,
  key: string,
  start: number,
  factor = 1.5,
): Backoff {
  return {
    key,
    delay:
      previous?.key === key
        ? Math.min(POLL_MAX_MS, Math.round(previous.delay * factor))
        : start,
  };
}

const hidden = () =>
  typeof document !== 'undefined' && document.visibilityState === 'hidden';

/**
 * Runs `poll` after `delay`; when the tab is hidden by then, as soon as it shows again.
 * Returns the function that cancels it.
 */
export function pollAfter(delay: number, poll: () => void): () => void {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const onVisible = () => {
    if (hidden()) return;
    document.removeEventListener('visibilitychange', onVisible);
    poll();
  };
  timer = setTimeout(() => {
    timer = undefined;
    if (hidden()) document.addEventListener('visibilitychange', onVisible);
    else poll();
  }, delay);
  return () => {
    clearTimeout(timer);
    if (typeof document !== 'undefined')
      document.removeEventListener('visibilitychange', onVisible);
  };
}
